import type { TilePosition } from '@shefira/shared';

/**
 * Playing without the mouse: the next target (Tab by default) and the
 * interaction key. Only what to aim at is chosen here, a highlight of this
 * client; the attack, the talk, the chest, the arch and the pickup go out as
 * the intents a click sends, and the server judges each one again.
 */

/** Something on a tile the keys can aim at: a monster, a townsperson, a chest… */
export interface Placed<T> {
  readonly x: number;
  readonly y: number;
  readonly value: T;
}

/** How far the interaction key reaches, in tiles (diagonals count): beside a townsperson. */
export const INTERACT_REACH = 2;

function distance(from: TilePosition, at: TilePosition): number {
  return (at.x - from.x) ** 2 + (at.y - from.y) ** 2;
}

/** Nearest first; at the same distance, the earlier in the list (a stable order). */
export function byDistance<T>(from: TilePosition, things: readonly Placed<T>[]): Placed<T>[] {
  return things
    .map((thing, index) => ({ thing, index, distance: distance(from, thing) }))
    .sort((a, b) => a.distance - b.distance || a.index - b.index)
    .map(({ thing }) => thing);
}

/**
 * The target after `current`, nearest first and round again; the nearest when
 * nothing, or something gone, was aimed at.
 */
export function nextTarget<T>(
  from: TilePosition,
  mobs: readonly Placed<T>[],
  current: T | undefined,
): T | undefined {
  const ordered = byDistance(from, mobs);
  if (ordered.length === 0) return undefined;
  const at = ordered.findIndex((mob) => mob.value === current);
  return ordered[(at + 1) % ordered.length]?.value;
}

/** The nearest thing within `reach` tiles (diagonals count), if any. */
export function nearestWithin<T>(
  from: TilePosition,
  things: readonly Placed<T>[],
  reach: number = INTERACT_REACH,
): T | undefined {
  const near = things.filter(
    (thing) => Math.max(Math.abs(thing.x - from.x), Math.abs(thing.y - from.y)) <= reach,
  );
  return byDistance(from, near)[0]?.value;
}
